import React from "react";
import {
  InfoOutlined,
  PersonOutlined,
  PaymentOutlined,
  AccountBalanceOutlined,
  AccessTimeOutlined,
  UpdateOutlined,
  LocalAtmOutlined,
  MonetizationOnOutlined,
  Inventory2Outlined,
  CalendarTodayOutlined,
  PhoneOutlined,
  ReceiptOutlined,
  StoreOutlined,
} from "@mui/icons-material";
import { Box, Grid, Typography, Divider } from "@mui/material";

import InformationTile from "components/Shared/InformationTile";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";
import Status from "components/Shared/Status";
import { useDeliveryCollectionDetailContext } from "contexts/PaymentContexts/DeliveryCollectionDetailContext";
import { localizeDateTimeShort } from "utils/dateFormats";
import OpenInNewTab from "components/Shared/OpenInNewTab";

const DeliveryCollectionBasicDetail = () => {
  const { deliveryCollectionDetail, loadingDeliveryCollection } =
    useDeliveryCollectionDetailContext();

  return !loadingDeliveryCollection ? (
    <Box padding={3}>
      <Grid container spacing={4}>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <Inventory2Outlined color="primary" />}
            title="Delivery Collection ID"
            subtitle={deliveryCollectionDetail?.deliveryCollectionId || "N/A"}
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <PersonOutlined color="primary" />}
            title="Delivered By"
            subtitle={
              deliveryCollectionDetail?.responsibleUser?.name
                ? deliveryCollectionDetail.responsibleUser.name
                : "Driver"
            }
          />
        </Grid>

        {!deliveryCollectionDetail?.responsibleUser &&
          deliveryCollectionDetail?.driver?._id && (
            <Grid item xs={3}>
              <InformationTile
                icon={() => <PersonOutlined color="primary" />}
                title="Driver"
                subtitle={
                  deliveryCollectionDetail?.driver?._id ? (
                    <OpenInNewTab
                      label={deliveryCollectionDetail.driver.name}
                      link={`/app/crm/driver/${deliveryCollectionDetail.driver._id}/detail`}
                      disableIcon
                    />
                  ) : (
                    "N/A"
                  )
                }
              />
            </Grid>
          )}
        <Grid item xs={3}>
          <InformationTile
            icon={() => <PaymentOutlined color="primary" />}
            title="Collection Mode"
            subtitle={deliveryCollectionDetail?.collectionMode?.name || "N/A"}
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <LocalAtmOutlined color="primary" />}
            title="Actual Amount"
            subtitle={
              deliveryCollectionDetail?.actualAmount?.toFixed(2) || "0.00"
            }
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <MonetizationOnOutlined color="primary" />}
            title="Collected Amount"
            subtitle={
              deliveryCollectionDetail?.collectedAmount?.toFixed(2) || "0.00"
            }
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Remittance Remarks"
            subtitle={deliveryCollectionDetail?.remittanceRemarks || "N/A"}
          />
        </Grid>
        {deliveryCollectionDetail?.bankName && (
          <Grid item xs={3}>
            <InformationTile
              icon={() => <AccountBalanceOutlined color="primary" />}
              title="Bank Name"
              subtitle={deliveryCollectionDetail?.bankName?.name || "N/A"}
            />
          </Grid>
        )}
        <Grid item md={3}>
          <InformationTile
            icon={() => <AccessTimeOutlined color="primary" />}
            title="Requested Date"
            subtitle={
              deliveryCollectionDetail?.createdAt
                ? localizeDateTimeShort(deliveryCollectionDetail?.createdAt)
                : "N/A"
            }
          />
        </Grid>
        <Grid item md={3}>
          <InformationTile
            icon={() => <UpdateOutlined color="primary" />}
            title="Updated At"
            subtitle={
              deliveryCollectionDetail?.updatedAt
                ? localizeDateTimeShort(deliveryCollectionDetail?.updatedAt)
                : "N/A"
            }
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Is Active"
          >
            <Status isActive={deliveryCollectionDetail?.isActive} />
          </InformationTile>
        </Grid>
      </Grid>
{deliveryCollectionDetail?.financeCollected && (
  <>
    <Divider sx={{ my: 4 }} />
    <Typography variant="h6" padding={2}>
      Finance Collection Details
    </Typography>
    <Grid container spacing={4}>
      <Grid item xs={3}>
        <InformationTile
          icon={() => <PersonOutlined color="primary" />}
          title="Created By"
          subtitle={
            deliveryCollectionDetail?.financeCollected?.createdBy
              ? deliveryCollectionDetail.financeCollected.createdBy.name
              : "N/A"
          }
        />
      </Grid>
      <Grid item xs={3}>
        <InformationTile
          icon={() => <PaymentOutlined color="primary" />}
          title="Collection Method"
          subtitle={
            deliveryCollectionDetail?.financeCollected?.collectionMethod?.name ||
            "N/A"
          }
        />
      </Grid>
      <Grid item xs={3}>
        <InformationTile
          icon={() => <AccessTimeOutlined color="primary" />}
          title="Finance Collected Date"
          subtitle={
            deliveryCollectionDetail?.financeCollected?.collectedDate
              ? localizeDateTimeShort(
                  deliveryCollectionDetail.financeCollected?.collectedDate
                )
              : "N/A"
          }
        />
      </Grid>
      {deliveryCollectionDetail?.financeCollected?.bankName?.name && (
        <Grid item xs={3}>
          <InformationTile
            icon={() => <AccountBalanceOutlined color="primary" />}
            title="Bank Name"
            subtitle={
              deliveryCollectionDetail.financeCollected.bankName?.name || "N/A"
            }
          />
        </Grid>
      )}

      {deliveryCollectionDetail?.financeCollected?.payerName && (
        <Grid item xs={3}>
          <InformationTile
            icon={() => <PersonOutlined color="primary" />}
            title="Payer Name"
            subtitle={
              deliveryCollectionDetail.financeCollected.payerName || "N/A"
            }
          />
        </Grid>
      )}

      {deliveryCollectionDetail?.financeCollected?.payerMobileNumber && (
        <Grid item xs={3}>
          <InformationTile
            icon={() => <PhoneOutlined color="primary" />}
            title="Payer Mobile Number"
            subtitle={
              deliveryCollectionDetail.financeCollected.payerMobileNumber ||
              "N/A"
            }
          />
        </Grid>
      )}

      {deliveryCollectionDetail?.financeCollected?.merchantName && (
        <Grid item xs={3}>
          <InformationTile
            icon={() => <StoreOutlined color="primary" />}
            title="Merchant Name"
            subtitle={
              deliveryCollectionDetail.financeCollected.merchantName || "N/A"
            }
          />
        </Grid>
      )}

      {/* {deliveryCollectionDetail?.financeCollected?.merchantTxnRef && (
        <Grid item xs={3}>
          <InformationTile
            icon={() => <ReceiptOutlined color="primary" />}
            title="Merchant Transaction Ref"
            subtitle={
              deliveryCollectionDetail.financeCollected.merchantTxnRef || "N/A"
            }
          />
        </Grid>
      )} */}
    </Grid>
  </>
)}

    </Box>
  ) : (
    <InfoGridSkeleton />
  );
};

export default DeliveryCollectionBasicDetail;
